/**
 * Common Icons
 * 
 * General-purpose icons used throughout the startup UI.
 * These icons are fundamental building blocks used in multiple contexts.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { getIconClasses } from '../iconUtils';

/**
 * SearchIcon - Magnifying glass for search functionality
 */
export const SearchIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

SearchIcon.propTypes = {
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl']),
  color: PropTypes.oneOf(['inherit', 'primary', 'secondary', 'success', 'warning', 'danger', 'neutral', 'white']),
  className: PropTypes.string,
};

/**
 * CheckIcon - Checkmark for success/confirmation states
 */
export const CheckIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
  </svg>
);

CheckIcon.propTypes = SearchIcon.propTypes;

/**
 * XIcon - X/Close icon for dismissing or canceling
 */
export const XIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

XIcon.propTypes = SearchIcon.propTypes;

/**
 * EyeIcon - Eye icon for visibility toggles (e.g., show password)
 */
export const EyeIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg
    className={getIconClasses(size, color, className)}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

EyeIcon.propTypes = SearchIcon.propTypes;

/**
 * EyeOffIcon - Eye with slash icon for hidden/obscured state (e.g., hide password)
 */
export const EyeOffIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg
    className={getIconClasses(size, color, className)}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.88 9.88a3 3 0 104.24 4.24" />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M10.73 5.08A10.43 10.43 0 0112 5c4.48 0 8.27 2.94 9.54 7a10.51 10.51 0 01-1.19 2.54"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M6.61 6.61A10.45 10.45 0 002.46 12c1.27 4.06 5.06 7 9.54 7a10.34 10.34 0 004.81-1.19"
    />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3l18 18" />
  </svg>
);

EyeOffIcon.propTypes = SearchIcon.propTypes;

/**
 * ChevronDownIcon - Downward pointing chevron for dropdowns/expansion
 */
export const ChevronDownIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
  </svg>
);

ChevronDownIcon.propTypes = SearchIcon.propTypes;

/**
 * ChevronRightIcon - Right pointing chevron for navigation/expansion
 */
export const ChevronRightIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
  </svg>
);

ChevronRightIcon.propTypes = SearchIcon.propTypes;

/**
 * ChevronUpIcon - Upward pointing chevron for collapse/up navigation
 */
export const ChevronUpIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
  </svg>
);

ChevronUpIcon.propTypes = SearchIcon.propTypes;

/**
 * ChevronLeftIcon - Left pointing chevron for back navigation
 */
export const ChevronLeftIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
  </svg>
);

ChevronLeftIcon.propTypes = SearchIcon.propTypes;

/**
 * GlobeIcon - Globe/world icon for website/internet concepts
 */
export const GlobeIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    viewBox="0 0 120 120" 
    fill="currentColor"
    {...props}
  >
    <path d="M60 120C26.9 120 0 93.1 0 60S26.9 0 60 0s60 26.9 60 60-26.9 60-60 60zM60 5C29.7 5 5 29.7 5 60s24.7 55 55 55 55-24.7 55-55S90.3 5 60 5z"/>
    <path d="M60 120c-19.3 0-34.4-26.4-34.4-60S40.7 0 60 0s34.4 26.4 34.4 60-15.1 60-34.4 60zM60 5C43.8 5 30.5 29.7 30.5 60s13.2 55 29.5 55 29.5-24.7 29.5-55S76.2 5 60 5z"/>
    <path d="M12.2 25.6h95.6v5H12.2zM12.2 89.5h95.6v5H12.2zM2.5 57.5h115v5H2.5z"/>
    <path d="M57.5 2.5h5v115h-5z"/>
  </svg>
);

GlobeIcon.propTypes = SearchIcon.propTypes;

/**
 * LoadingSpinner - Animated loading spinner
 * Uses inherit color by default to adapt to parent context (theme-aware)
 */
export const LoadingSpinner = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <div 
    className={`${getIconClasses(size, color, className)} animate-spin border-2 border-current border-t-transparent rounded-full`}
    {...props}
  />
);

LoadingSpinner.propTypes = SearchIcon.propTypes;

/**
 * UserProfileIcon - User profile/avatar icon
 */
export const UserProfileIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg
    className={getIconClasses(size, color, className)}
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
    {...props}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.25 9C8.25 6.92893 9.92893 5.25 12 5.25C14.0711 5.25 15.75 6.92893 15.75 9C15.75 11.0711 14.0711 12.75 12 12.75C9.92893 12.75 8.25 11.0711 8.25 9ZM12 6.75C10.7574 6.75 9.75 7.75736 9.75 9C9.75 10.2426 10.7574 11.25 12 11.25C13.2426 11.25 14.25 10.2426 14.25 9C14.25 7.75736 13.2426 6.75 12 6.75Z"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1.25 12C1.25 6.06294 6.06294 1.25 12 1.25C17.9371 1.25 22.75 6.06294 22.75 12C22.75 17.9371 17.9371 22.75 12 22.75C6.06294 22.75 1.25 17.9371 1.25 12ZM12 2.75C6.89137 2.75 2.75 6.89137 2.75 12C2.75 14.5456 3.77827 16.851 5.4421 18.5235C5.6225 17.5504 5.97694 16.6329 6.68837 15.8951C7.75252 14.7915 9.45416 14.25 12 14.25C14.5457 14.25 16.2474 14.7915 17.3115 15.8951C18.023 16.6329 18.3774 17.5505 18.5578 18.5236C20.2217 16.8511 21.25 14.5456 21.25 12C21.25 6.89137 17.1086 2.75 12 2.75ZM17.1937 19.6554C17.0918 18.4435 16.8286 17.5553 16.2318 16.9363C15.5823 16.2628 14.3789 15.75 12 15.75C9.62099 15.75 8.41761 16.2628 7.76815 16.9363C7.17127 17.5553 6.90811 18.4434 6.80622 19.6553C8.28684 20.6618 10.0747 21.25 12 21.25C13.9252 21.25 15.7131 20.6618 17.1937 19.6554Z"
    />
  </svg>
);

UserProfileIcon.propTypes = SearchIcon.propTypes;

/**
 * StarIcon - Star icon for ratings/favorites
 */
export const StarIcon = ({ size = 'md', color = 'inherit', className = '', filled = true, ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke={filled ? 'none' : 'currentColor'}
    strokeWidth={filled ? 0 : 2}
    {...props}
  >
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
  </svg>
);

StarIcon.propTypes = {
  ...SearchIcon.propTypes,
  filled: PropTypes.bool,
};

/**
 * CalendarIcon - Calendar icon for dates/scheduling
 */
export const CalendarIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

CalendarIcon.propTypes = SearchIcon.propTypes;

/**
 * ClockIcon - Clock/time icon for time-based messaging (e.g., "coming soon")
 */
export const ClockIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg
    className={getIconClasses(size, color, className)}
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
    {...props}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M4 12C4 7.58172 7.58172 4 12 4C12.5523 4 13 3.55228 13 3C13 2.44772 12.5523 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C14.7611 22 17.2625 20.8796 19.0711 19.0711C19.4616 18.6805 19.4616 18.0474 19.0711 17.6569C18.6805 17.2663 18.0474 17.2663 17.6569 17.6569C16.208 19.1057 14.2094 20 12 20C7.58172 20 4 16.4183 4 12ZM13 6C13 5.44772 12.5523 5 12 5C11.4477 5 11 5.44772 11 6V12C11 12.2652 11.1054 12.5196 11.2929 12.7071L14.2929 15.7071C14.6834 16.0976 15.3166 16.0976 15.7071 15.7071C16.0976 15.3166 16.0976 14.6834 15.7071 14.2929L13 11.5858V6ZM21.7483 15.1674C21.535 15.824 20.8298 16.1833 20.1732 15.97C19.5167 15.7566 19.1574 15.0514 19.3707 14.3949C19.584 13.7383 20.2892 13.379 20.9458 13.5923C21.6023 13.8057 21.9617 14.5108 21.7483 15.1674ZM21.0847 11.8267C21.7666 11.7187 22.2318 11.0784 22.1238 10.3966C22.0158 9.71471 21.3755 9.2495 20.6937 9.3575C20.0118 9.46549 19.5466 10.1058 19.6546 10.7877C19.7626 11.4695 20.4029 11.9347 21.0847 11.8267ZM20.2924 5.97522C20.6982 6.53373 20.5744 7.31544 20.0159 7.72122C19.4574 8.127 18.6757 8.00319 18.2699 7.44468C17.8641 6.88617 17.9879 6.10446 18.5464 5.69868C19.1049 5.2929 19.8867 5.41671 20.2924 5.97522ZM17.1997 4.54844C17.5131 3.93333 17.2685 3.18061 16.6534 2.86719C16.0383 2.55378 15.2856 2.79835 14.9722 3.41346C14.6588 4.02858 14.9033 4.78129 15.5185 5.09471C16.1336 5.40812 16.8863 5.16355 17.1997 4.54844Z"
    />
  </svg>
);

ClockIcon.propTypes = SearchIcon.propTypes;

/**
 * MagicBallIcon v2.0 - CSS-driven hover effects via data attributes
 * Responds to parent panel hover via CSS :has() and data attributes
 */
export const MagicBallIcon = ({ size = 'lg', color = 'inherit', className = '', pixelSize, ...props }) => {
  const idRef = React.useRef(`mb-grad-${Math.random().toString(36).slice(2, 8)}`);
  const gradId = idRef.current;
  
  const iconClasses = pixelSize 
    ? `${className} cursor-pointer magic-ball-icon`.trim()
    : `${getIconClasses(size, color, className)} cursor-pointer magic-ball-icon`;
  
  return (
    <svg
      className={iconClasses}
      viewBox="-8 -8 40 40"
      xmlns="http://www.w3.org/2000/svg"
      width={pixelSize}
      height={pixelSize}
      style={pixelSize ? { width: `${pixelSize}px`, height: `${pixelSize}px`, minWidth: `${pixelSize}px`, minHeight: `${pixelSize}px` } : undefined}
      {...props}
    >
      <defs>
        <radialGradient id={gradId} cx="50%" cy="40%" r="60%">
          <stop 
            offset="0%" 
            stopColor="#ffffff" 
            stopOpacity="0.92" 
            className="mb-center-stop"
            style={{ 
              transition: 'stop-color 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
            }} 
          />
          <stop offset="28%" stopColor="#34D399" stopOpacity="0.98" />
          <stop offset="55%" stopColor="#2DD4BF" stopOpacity="0.98" />
          <stop offset="78%" stopColor="#38BDF8" stopOpacity="0.98" />
          <stop offset="100%" stopColor="#3B82F6" stopOpacity="1" />
        </radialGradient>
        <filter id={`${gradId}-blur`}>
          <feGaussianBlur in="SourceGraphic" stdDeviation="1.5" />
        </filter>
      </defs>
      <style>{`
        @keyframes mb-float { 
          0%, 100% { transform: translateY(0) rotate(0deg); } 
          50% { transform: translateY(-2px) rotate(2deg); } 
        }
        @keyframes mb-float-subtle { 
          0%, 100% { transform: translateY(0); } 
          50% { transform: translateY(-1px); } 
        }
        @keyframes mb-squish { 
          0%, 100% { transform: scale(1, 1); } 
          50% { transform: scale(0.985, 1.02); } 
        }
        @keyframes mb-orbit { 
          0% { transform: rotate(0deg); } 
          100% { transform: rotate(360deg); } 
        }
        @keyframes mb-emerald-pulse { 
          0% { 
            opacity: 0; 
            transform: scale(1) translateY(0); 
          } 
          15% {
            opacity: 0.3;
            transform: scale(1.15) translateY(-0.5px);
          }
          50% { 
            opacity: 0.6; 
            transform: scale(1.4) translateY(-1px); 
          } 
          85% {
            opacity: 0.3;
            transform: scale(1.15) translateY(-0.5px);
          }
          100% { 
            opacity: 0; 
            transform: scale(1) translateY(0); 
          } 
        }
        .mb-root { 
          transform-origin: 12px 12px; 
          animation: mb-float 4s ease-in-out infinite; 
        }
        .mb-body { 
          transform-origin: 12px 12px; 
          animation: mb-squish 2.8s ease-in-out infinite; 
        }
        .mb-orbit { 
          transform-origin: 12px 12px; 
          animation: mb-orbit 6.5s linear infinite; 
        }
        .mb-glow { 
          transition: stroke 0.3s ease, opacity 0.3s ease;
          transform-origin: 12px 12px;
          animation: mb-float-subtle 4s ease-in-out infinite;
        }
        .mb-emerald-outer { 
          transform-origin: 12px 12px;
          opacity: 0;
          transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        /* Panel hover activation via CSS selectors */
        [data-panel-hovered="true"] .magic-ball-icon .mb-center-stop {
          stop-color: #FDE047;
        }
        [data-panel-hovered="true"] .magic-ball-icon .mb-emerald-outer,
        .magic-ball-icon:hover .mb-emerald-outer,
        svg.magic-ball-icon:hover .mb-emerald-outer { 
          opacity: 1;
          animation: mb-emerald-pulse 4.5s ease-in-out infinite;
        }
      `}</style>
      {/* Outer subtle glow ring */}
      <circle 
        className="mb-glow" 
        cx="12" 
        cy="12" 
        r="10.2" 
        fill="none" 
        stroke="#7DD3FC" 
        strokeOpacity="0.3" 
        strokeWidth="0.6" 
      />
      {/* Emerald pulsing glow OUTSIDE the ball with blur */}
      <circle 
        className="mb-emerald-outer" 
        cx="12" 
        cy="12" 
        r="11.5" 
        fill="none" 
        stroke="#34D399" 
        strokeOpacity="0.7" 
        strokeWidth="3" 
        filter={`url(#${gradId}-blur)`} 
      />
      <g className="mb-root">
        {/* Core ball */}
        <circle className="mb-body" cx="12" cy="12" r="9" fill={`url(#${gradId})`} />
        {/* Inner depth ring */}
        <circle cx="12" cy="12" r="7.25" fill="none" stroke="#ffffff" strokeOpacity="0.18" strokeWidth="0.5" />
        {/* Moving shine */}
        <g className="mb-orbit">
          <circle cx="12" cy="6" r="1.15" fill="#ffffff" fillOpacity="0.9" />
          <circle cx="12" cy="6" r="2.4" fill="#ffffff" fillOpacity="0.12" />
        </g>
        {/* Subtle bottom shadow */}
        <ellipse cx="12" cy="18.3" rx="5.2" ry="1.3" fill="#1F2937" fillOpacity="0.12" />
      </g>
    </svg>
  );
};

MagicBallIcon.propTypes = SearchIcon.propTypes;

/**
 * LayoutIcon - Layout/grid icon for component layout controls
 */
export const LayoutIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="currentColor" 
    viewBox="0 0 20 20"
    {...props}
  >
    <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
  </svg>
);

LayoutIcon.propTypes = SearchIcon.propTypes;
